'use client';

import { useEffect, useImperativeHandle, useMemo, useRef, type Ref } from 'react';
import { useTranslations } from '@/i18n/translations';
import { Avatar, Caption, I, Icon, Popover, Row, Stack, Thumbnail } from '@leanin/ui';
import { MENTION_ALL_HANDLE } from '@leanin/shared';
import { useMembers, type MemberView } from '../../lib/queries/identity';
import { useDebouncedValue } from '../../lib/use-debounced-value';

/** Display name shown in the picker and on inserted mention chips: "First
 *  Last" from the viewer-visible fields. Mirrors the server's
 *  MentionView.name composition. */
export function mentionDisplayName(member: MemberView): string {
  return [member.firstName, member.lastName].filter(Boolean).join(' ');
}

/** The group a whole-group "@all" mention addresses on this surface. */
export type MentionAllGroup = {
  readonly name: string;
};

/** What the picker committed. `all` is the synthetic whole-group row — it
 *  resolves to the reserved `@all` handle, not to a member, so it carries no
 *  MemberView. */
export type MentionPick =
  | { readonly kind: 'member'; readonly member: MemberView }
  | { readonly kind: 'all'; readonly group: MentionAllGroup };

export type MentionPopoverHandle = {
  itemCount: () => number;
  /** True while suggestions for the current query are still being fetched. */
  isPending: () => boolean;
  /** Insert the active suggestion; false when there is nothing to select. */
  selectActive: () => boolean;
};

type Props = {
  anchorRect: DOMRect;
  query: string;
  activeIndex: number;
  onSelect: (pick: MentionPick) => void;
  onDismiss: () => void;
  handle?: Ref<MentionPopoverHandle>;
  /** The group "@all" would notify. Its presence IS the permission: set only
   *  when the viewer may address the group (the server re-checks leadership
   *  on write), and it labels the synthetic "@all" row. */
  mentionAllGroup?: MentionAllGroup;
  /** When set, only suggest people who can see content scoped to this group
   *  (members / child-circle members). Undefined on the global feed → anyone. */
  audienceGroupId?: string;
  /** Member to pin to the top of the unfiltered recommended set (the post
   *  author, for a reply/comment). Ignored once a typed query is narrowing
   *  results — relevance ranking owns that order. */
  priorityMember?: MemberView;
};

const MAX_HEIGHT = 240;

export function MentionPopover({
  anchorRect,
  query,
  activeIndex,
  onSelect,
  onDismiss,
  handle,
  audienceGroupId,
  priorityMember,
  mentionAllGroup,
}: Props) {
  const t = useTranslations('Feed');
  // The trim folds "lindsay" / "lindsay " into one query while the user is
  // mid-way through a two-word narrowing. Debounce so a burst of keystrokes
  // collapses to one request instead of firing a member search per key.
  const trimmedQuery = query.trim();
  const debouncedQuery = useDebouncedValue(trimmedQuery, 200);
  // The server's Class-F search predicate only engages at >= 2 chars; a
  // 1-char query returns the unfiltered recommended set, so firing it would
  // look like the typed letter did nothing. Treat 0–1 chars as the bare-"@"
  // recommended picker (q undefined → same query key, no refetch/flash) and
  // narrow only once the query can actually filter.
  const effectiveQuery = debouncedQuery.length >= 2 ? debouncedQuery : undefined;
  const { data, isLoading, isFetching } = useMembers({
    q: effectiveQuery,
    pageSize: 5,
    // Relevance-ranked, not the service's newest-account default: under
    // 'recent', a member sharing a first name with pageSize newer accounts
    // could never surface, making them untaggable.
    sort: 'recommended',
    ...(audienceGroupId !== undefined ? { audienceGroupId } : {}),
  });
  const members = useMemo(() => {
    const base = (data?.items ?? []) as MemberView[];
    // Pin the post author to the top of the bare-"@" recommended set: in a
    // reply/comment the person you're most likely tagging is whoever wrote the
    // post. Once a typed query is narrowing the list the server's relevance
    // ranking owns the order, so only pin when there is no active query.
    // Dedupe so an author already in the recommended set isn't listed twice.
    if (priorityMember === undefined || effectiveQuery !== undefined) return base;
    return [priorityMember, ...base.filter((m) => m.id !== priorityMember.id)];
  }, [data?.items, priorityMember, effectiveQuery]);

  // The "@all" row is offered while what has been typed is still a prefix of
  // the reserved handle (including the bare "@"), so it behaves like any
  // other suggestion narrowing under the caret. It is always first, so its
  // index never shifts as member results arrive.
  const allRowGroup =
    mentionAllGroup !== undefined &&
    MENTION_ALL_HANDLE.startsWith(trimmedQuery.toLowerCase())
      ? mentionAllGroup
      : null;
  const rows = useMemo<ReadonlyArray<MentionPick>>(
    () => [
      ...(allRowGroup !== null ? [{ kind: 'all', group: allRowGroup } as const] : []),
      ...members.map((member) => ({ kind: 'member', member }) as const),
    ],
    [allRowGroup, members],
  );
  const listRef = useRef<HTMLDivElement>(null);

  // The suggestions on screen don't reflect the currently typed query while
  // (a) the 200ms debounce hasn't caught up (`debouncedQuery !== trimmedQuery`)
  // or (b) react-query is still fetching — including the `keepPreviousData`
  // placeholder transition, where `isLoading` is false but the visible list is
  // the PREVIOUS prefix's results. The editor's key handler consults this to
  // hold Enter/Arrow while loading, and `selectActive` refuses to commit a
  // stale suggestion, so a fast typist never inserts the wrong member or
  // splits a paragraph mid-flight.
  const isStale = debouncedQuery !== trimmedQuery || isFetching;

  useImperativeHandle(
    handle,
    () => ({
      itemCount: () => rows.length,
      isPending: () => isStale,
      selectActive: () => {
        const row = rows[activeIndex];
        if (row === undefined) return false;
        // The "@all" row is local, not fetched — an in-flight member search
        // can't make it stale, and it can't move off index 0.
        if (row.kind === 'all') {
          onSelect(row);
          return true;
        }
        if (isStale) return false;
        onSelect(row);
        return true;
      },
    }),
    [rows, activeIndex, onSelect, isStale],
  );

  useEffect(() => {
    const el = listRef.current;
    if (el === null) return;
    const active = el.children[activeIndex] as HTMLElement | undefined;
    active?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  // Radix re-positions whenever the anchor's bounding rect changes; tracking
  // anchorRect identity means a fresh caret rect on every keystroke produces
  // a fresh virtual anchor object, which Radix sees as a position update.
  const virtualAnchor = useMemo(
    () => ({ getBoundingClientRect: () => anchorRect }),
    [anchorRect],
  );

  // A member search that returns nothing must not hide the "@all" row.
  if (!isLoading && rows.length === 0) return null;

  return (
    <Popover
      open
      onOpenChange={(next) => {
        if (!next) onDismiss();
      }}
      virtualAnchor={virtualAnchor}
      side="bottom"
      align="start"
      sideOffset={4}
      onOpenAutoFocus={(event) => event.preventDefault()}
      onCloseAutoFocus={(event) => event.preventDefault()}
    >
      <div
        ref={listRef}
        role="listbox"
        aria-label={t('mention.suggestionsAria')}
        style={{
          width: 248,
          maxHeight: MAX_HEIGHT,
          overflowY: 'auto',
        }}
      >
        {isLoading && rows.length === 0 ? (
          <Stack p="md">
            <Caption>{t('mention.searching')}</Caption>
          </Stack>
        ) : (
          rows.map((row, i) => {
            const primary =
              row.kind === 'all'
                ? t('mention.everyoneIn', { group: row.group.name })
                : mentionDisplayName(row.member);
            const secondary =
              row.kind === 'all' ? `@${MENTION_ALL_HANDLE}` : `@${row.member.handle}`;
            return (
              <button
                key={row.kind === 'all' ? 'mention-all' : row.member.id}
                type="button"
                role="option"
                aria-selected={i === activeIndex}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onSelect(row);
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  background: i === activeIndex ? 'var(--accent)' : 'transparent',
                  border: 'none',
                  borderRadius: 0,
                  cursor: 'pointer',
                  textAlign: 'start',
                }}
              >
                <Row gap="sm" align="center" px="md" py="sm">
                  {row.kind === 'all' ? (
                    <Thumbnail tone="neutral" size={32} radius="50%">
                      <Icon size={16}>{I.users}</Icon>
                    </Thumbnail>
                  ) : (
                    <Avatar src={row.member.photoUrl} name={primary} size={32} />
                  )}
                  <Stack gap="none">
                    <span style={{ font: 'var(--type-label)', color: 'var(--foreground)' }}>
                      {primary}
                    </span>
                    <span style={{ font: 'var(--type-caption)', color: 'var(--muted-foreground)' }}>
                      {secondary}
                    </span>
                  </Stack>
                </Row>
              </button>
            );
          })
        )}
      </div>
    </Popover>
  );
}
